import { useState, useEffect, type FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { resetPassword } from '@/lib/api';
import { motion } from 'framer-motion';
import { KeyRound, CheckCircle2, AlertCircle, Eye, EyeOff } from 'lucide-react';

export function ResetPassword() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const token = searchParams.get('token') || '';

    const [password, setPassword] = useState('');
    const [confirm, setConfirm] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) setError('リンクが無効です。お手数ですが、もう一度再発行をお試しください。');
    }, [token]);

    const extractDetail = (err: any, fallback: string) => {
        const detail = err?.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        if (Array.isArray(detail)) return detail.map((e: any) => e.msg).join(', ');
        if (detail && typeof detail === 'object') return detail.message || JSON.stringify(detail);
        return fallback;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError('');
        if (!token) { setError('リンクが無効です。'); return; }
        if (password.length < 8) { setError('パスワードは8文字以上でご設定ください。'); return; }
        if (password !== confirm) { setError('確認用パスワードが一致しません。'); return; }
        setIsSubmitting(true);
        try {
            await resetPassword(token, password);
            setDone(true);
        } catch (err: any) {
            setError(extractDetail(err, 'リンクが無効か、有効期限（1時間）が切れています。お手数ですが、もう一度お手続きください。'));
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#0c0a09] font-serif relative overflow-hidden p-4">
            <div className="absolute inset-0 z-0 opacity-40 bg-[url('https://images.unsplash.com/photo-1506459225024-1428097a7e18?q=80&w=2574&auto=format&fit=crop')] bg-cover bg-center mix-blend-overlay"></div>
            <div className="absolute inset-0 z-0 bg-gradient-to-t from-black via-black/80 to-transparent"></div>

            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8 }}
                className="w-full max-w-md p-1 relative z-10"
            >
                <div className="bg-[#1c1917] border border-[#d4af37] shadow-[0_0_50px_rgba(212,175,55,0.1)] rounded-sm p-8 text-[#d4af37]">
                    <div className="text-center mb-8">
                        <KeyRound className="mx-auto text-[#d4af37] mb-4" size={36} />
                        <h1 className="text-2xl font-bold tracking-widest text-[#fcd34d]">新しいルームキーの設定</h1>
                        <div className="h-px w-24 bg-[#d4af37] mx-auto mt-6 opacity-50"></div>
                    </div>

                    {done ? (
                        <div className="text-center py-6">
                            <div className="flex justify-center mb-5">
                                <div className="w-16 h-16 bg-[#d4af37]/20 rounded-full flex items-center justify-center border border-[#d4af37]">
                                    <CheckCircle2 className="w-8 h-8 text-[#fcd34d]" />
                                </div>
                            </div>
                            <p className="text-[#d4af37]/90 leading-relaxed tracking-wider">
                                パスワードを再設定しました。<br />
                                新しいパスワードでログインしてください。
                            </p>
                            <Button
                                type="button"
                                onClick={() => navigate('/login')}
                                className="w-full mt-8 bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest"
                            >
                                ログイン画面へ
                            </Button>
                        </div>
                    ) : (
                        <form className="space-y-6" onSubmit={handleSubmit}>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">新しいパスワード（8文字以上）</label>
                                <div className="relative">
                                    <Input
                                        type={showPassword ? 'text' : 'password'}
                                        className="bg-[#0c0a09] border-[#d4af37]/30 text-[#fcd34d] placeholder:text-[#d4af37]/20 h-12 pl-4 pr-10 focus:ring-[#d4af37] focus:border-[#d4af37]"
                                        placeholder="新しいパスワード"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        autoComplete="new-password"
                                        required
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#d4af37]/50 hover:text-[#d4af37]"
                                    >
                                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                    </button>
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs uppercase tracking-widest mb-1 opacity-70">パスワード（確認）</label>
                                <Input
                                    type={showPassword ? 'text' : 'password'}
                                    className="bg-[#0c0a09] border-[#d4af37]/30 text-[#fcd34d] placeholder:text-[#d4af37]/20 h-12 pl-4 focus:ring-[#d4af37] focus:border-[#d4af37]"
                                    placeholder="もう一度入力"
                                    value={confirm}
                                    onChange={(e) => setConfirm(e.target.value)}
                                    autoComplete="new-password"
                                    required
                                />
                            </div>

                            {error && (
                                <div className="text-red-400 text-sm text-center border border-red-900/50 bg-red-950/20 p-3 flex items-center gap-2 justify-center">
                                    <AlertCircle size={18} />
                                    <span>{error}</span>
                                </div>
                            )}

                            <motion.button
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                type="submit"
                                disabled={isSubmitting || !token}
                                className="w-full bg-[#d4af37] hover:bg-[#ffe58f] text-black font-serif font-bold h-12 uppercase tracking-widest transition-all duration-300 flex items-center justify-center gap-3 disabled:opacity-60"
                            >
                                {isSubmitting ? <span className="animate-pulse">設定中...</span> : <span>パスワードを再設定する</span>}
                            </motion.button>
                        </form>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
